import Avo from 'analytics/Avo';
import type { PricingList } from 'api/types/pricing.types';
import type { CarouselItem } from 'components/molecules/Carousel';
import { useGlobalConfig } from 'pages/queries/global.queries';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { splitCommaSeparatedValues } from 'utils/helpers/extras';
import { trackEvent } from 'wrappers/reporting';

import type { ServiceContentType } from '../home/components/ServiceTypeOfferSheet';

interface UseDiscoveryCardPressHandlerProps {
  setWebSrc: (src: string) => void;
  setShow: (show: boolean) => void;
  openPricingSheet?: () => void;
  toggleOnboardingSheet?: () => void;
  onOpenServiceOfferSheet?: (type: ServiceContentType) => void;
}

export const useDiscoveryCardPressHandler = ({
  openPricingSheet,
  toggleOnboardingSheet,
  onOpenServiceOfferSheet,
  setWebSrc,
  setShow,
}: UseDiscoveryCardPressHandlerProps) => {
  const { data: appConfig } = useGlobalConfig();
  const navigate = useNavigate();

  const [navigateCleanPressPricingKeysPWA, navigateHomeCarePricingKeysPWA, navigateShoePricingKeysPWA, navigateWashFoldPricingKeysPWA] = useMemo(
    () => [
      splitCommaSeparatedValues(appConfig?.navigateCleanPressPricingKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.navigateHomeCarePricingKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.navigateShoePricingKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.navigateWashFoldPricingKeysPWA ?? ''),
    ],
    [appConfig]
  );

  const [
    alwaysTriggerPricingToKeysPWA,
    alwaysTriggerStepGuideToKeysPWA,
    alwaysTriggerShoesServiceSheetKeysPWA,
    alwaysTriggerWashAndFoldServiceSheetKeysPWA,
    alwaysTriggerBedAndBathServiceSheetKeysPWA,
    alwaysTriggerAED20ServiceSheetKeysPWA,
    alwaysTriggerInactiveCustomerSheetKeysPWA,
  ] = useMemo(
    () => [
      splitCommaSeparatedValues(appConfig?.alwaysTriggerPricingToKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.alwaysTriggerStepGuideToKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.alwaysTriggerShoesServiceSheetKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.alwaysTriggerWashAndFoldServiceSheetKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.alwaysTriggerBedAndBathServiceSheetKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.alwaysTriggerAED20ServiceSheetKeysPWA ?? ''),
      splitCommaSeparatedValues(appConfig?.alwaysTriggerInactiveCustomerSheetKeysPWA ?? ''),
    ],
    [appConfig]
  );

  const getPricingServiceTypeByItemKey = useCallback(
    (itemKey: string): PricingList.ServiceTypes | null => {
      if (navigateCleanPressPricingKeysPWA.includes(itemKey)) {
        return 'CLEAN_PRESS';
      }
      if (navigateHomeCarePricingKeysPWA.includes(itemKey)) {
        return 'HOME_CARE';
      }
      if (navigateShoePricingKeysPWA.includes(itemKey)) {
        return 'SHOE_CLEANING';
      }
      if (navigateWashFoldPricingKeysPWA.includes(itemKey)) {
        return 'WASH_FOLD';
      }
      return null;
    },
    [navigateCleanPressPricingKeysPWA, navigateHomeCarePricingKeysPWA, navigateShoePricingKeysPWA, navigateWashFoldPricingKeysPWA, setShow, setWebSrc]
  );

  const onPressFAQ = useCallback(
    (item: CarouselItem) => () => {
      const itemKey = item.key.toLocaleLowerCase();
      const itemKeyPricingServiceType = getPricingServiceTypeByItemKey(itemKey);
      Avo.discoveryCardClicked({ cardKey: item.key });
      if (alwaysTriggerPricingToKeysPWA.includes(itemKey)) {
        trackEvent('Clicked View Pricing from Discovery Carousel');
        Avo.viewPricing({ screen: 'Discovery Carousel' });
        openPricingSheet?.();
      } else if (alwaysTriggerStepGuideToKeysPWA.includes(itemKey)) {
        trackEvent('Open Step Guide from Discovery Carousel');
        Avo.openOnboardingSheet();
        toggleOnboardingSheet?.();
      } else if (alwaysTriggerShoesServiceSheetKeysPWA.includes(itemKey)) {
        onOpenServiceOfferSheet?.('SHOE_CLEANING');
      } else if (alwaysTriggerWashAndFoldServiceSheetKeysPWA.includes(itemKey)) {
        onOpenServiceOfferSheet?.('WASH_FOLD');
      } else if (alwaysTriggerBedAndBathServiceSheetKeysPWA.includes(itemKey)) {
        onOpenServiceOfferSheet?.('HOME_CARE');
      } else if (alwaysTriggerAED20ServiceSheetKeysPWA.includes(itemKey)) {
        onOpenServiceOfferSheet?.('AED30');
      } else if (alwaysTriggerInactiveCustomerSheetKeysPWA.includes(itemKey)) {
        onOpenServiceOfferSheet?.('DORMANT_USER');
      } else if (item.link) {
        setWebSrc(item.link);
        setShow(true);
      } else if (item.key === 'firstTimePromo') {
        openPricingSheet?.();
      } else if (itemKeyPricingServiceType) {
        navigate(`/pricing/${itemKeyPricingServiceType}`);
      }
    },
    [
      alwaysTriggerPricingToKeysPWA,
      alwaysTriggerStepGuideToKeysPWA,
      alwaysTriggerShoesServiceSheetKeysPWA,
      alwaysTriggerWashAndFoldServiceSheetKeysPWA,
      alwaysTriggerBedAndBathServiceSheetKeysPWA,
      alwaysTriggerAED20ServiceSheetKeysPWA,
      alwaysTriggerInactiveCustomerSheetKeysPWA,
      onOpenServiceOfferSheet,
      openPricingSheet,
      toggleOnboardingSheet,
      navigate,
      setShow,
      setWebSrc,
      getPricingServiceTypeByItemKey,
    ]
  );

  return {
    onPressFAQ,
  };
};
